/**
 * USGS Mineral Resource Data System (MRDS) occurrence screen.
 *
 * This provider reports public occurrence records only. It deliberately does
 * not infer mineral ownership, severance, leases, reservations, or surface-use
 * rights; those facts are accepted only from a client/county/title document.
 */
import type { DataProvider, ProviderGeoContext, ProviderResult } from './types';
import { CACHE_TTL, fetchWithTimeout, validateCoords } from './types';
import {
  haversineMiles,
  pointInPolygon,
  pointToPolygonDistanceMiles,
  searchEnvelope,
  type GeoPoint,
} from './siteDiligenceGeometry';

export const USGS_MRDS_LAYER_URL =
  'https://energy.usgs.gov/arcgis/rest/services/Hosted/Mineral_Resource_Data_System/FeatureServer/0';
export const USGS_MRDS_RECORD_LIMIT = 2_000;
const DETAILS_LIMIT = 25;

export interface MrdsOccurrenceSummary {
  featureId: string;
  name: string;
  developmentStatus: string | null;
  distanceMi: number;
  onParcel: boolean | null;
  commodities: string[];
  sourceUrl?: string;
}

export interface MineralResourcesScreenData {
  occurrenceCountOnParcel: number | null;
  occurrenceCountWithin1Mi: number;
  occurrenceCountWithin5Mi: number;
  nearestDistanceMi: number | null;
  commodities: string[];
  occurrences: MrdsOccurrenceSummary[];
  queriedFeatureCount: number;
  truncated: boolean;
  sourceVintage: string;
  screeningOnly: true;
}

interface ArcGisFeature {
  attributes?: Record<string, unknown>;
  geometry?: { x?: number; y?: number };
}

interface ArcGisResponse {
  features?: ArcGisFeature[];
  exceededTransferLimit?: boolean;
  error?: { message?: string };
}

type Fetcher = (url: string, timeoutMs?: number) => Promise<Response>;

function finiteString(value: unknown, max = 300): string | null {
  if (typeof value !== 'string') return null;
  const clean = value.replace(/[\u0000-\u001f]+/g, ' ').trim();
  return clean ? clean.slice(0, max) : null;
}

/** Bounded labels for common codes in the official MRDS code list. */
const MRDS_COMMODITY_LABEL: Readonly<Record<string, string>> = {
  AG: 'Silver',
  AU: 'Gold',
  CU: 'Copper',
  FE: 'Iron',
  GEO: 'Geothermal',
  LI: 'Lithium',
  PB: 'Lead',
  RAE: 'Rare earths',
  SDG: 'Sand and gravel',
  U: 'Uranium',
  ZN: 'Zinc',
};

function commodityNames(attributes: Record<string, unknown>): string[] {
  const names = new Set<string>();
  const codes = finiteString(attributes.code_list, 256);
  if (codes) {
    for (const rawCode of codes.split(/\s+/).map(v => v.trim()).filter(Boolean)) {
      const code = rawCode.replace(/\?$/, '').toUpperCase();
      if (!/^[A-Z0-9]{1,8}$/.test(code)) continue;
      names.add(MRDS_COMMODITY_LABEL[code] ?? `MRDS commodity code ${code}`);
    }
  }
  return [...names].sort((a, b) => a.localeCompare(b)).slice(0, 30);
}

function buildQueryUrl(centre: GeoPoint, boundary: readonly GeoPoint[] | undefined): string {
  const box = searchEnvelope(centre, boundary, 5);
  const params = new URLSearchParams({
    where: '1=1',
    geometry: `${box.minLng},${box.minLat},${box.maxLng},${box.maxLat}`,
    geometryType: 'esriGeometryEnvelope',
    inSR: '4326',
    outSR: '4326',
    spatialRel: 'esriSpatialRelIntersects',
    outFields: 'objectid_1,dep_id,site_name,dev_stat,code_list,grade',
    returnGeometry: 'true',
    resultRecordCount: String(USGS_MRDS_RECORD_LIMIT),
    f: 'json',
  });
  return `${USGS_MRDS_LAYER_URL}/query?${params.toString()}`;
}

export async function fetchUsgsMrdsScreen(
  lat: number,
  lng: number,
  geo?: ProviderGeoContext,
  fetcher: Fetcher = fetchWithTimeout,
): Promise<ProviderResult<MineralResourcesScreenData>> {
  validateCoords(lat, lng);
  const centre = geo?.centroid ?? { lat, lng };
  const boundary = geo?.boundary && geo.boundary.length >= 3 ? geo.boundary : undefined;
  const response = await fetcher(buildQueryUrl(centre, boundary), 30_000);
  if (!response.ok) throw new Error(`USGS MRDS HTTP ${response.status}`);
  const payload = await response.json() as ArcGisResponse;
  if (payload.error) throw new Error(`USGS MRDS ArcGIS error: ${payload.error.message ?? 'unknown error'}`);

  const byId = new Map<string, MrdsOccurrenceSummary>();
  for (const feature of payload.features ?? []) {
    const x = feature.geometry?.x;
    const y = feature.geometry?.y;
    if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
    const point = { lat: y as number, lng: x as number };
    const attrs = feature.attributes ?? {};
    const featureId = finiteString(attrs.dep_id, 80) ?? `mrds-${byId.size + 1}`;
    const distanceMi = boundary
      ? pointToPolygonDistanceMiles(point, boundary)
      : haversineMiles(centre, point);
    const onParcel = boundary ? pointInPolygon(point, boundary) : null;
    const sourceUrl = /^[A-Za-z0-9_-]{1,80}$/.test(featureId)
      ? `https://mrdata.usgs.gov/mrds/show-mrds.php?dep_id=${encodeURIComponent(featureId)}`
      : undefined;
    const candidate: MrdsOccurrenceSummary = {
      featureId,
      name: finiteString(attrs.site_name, 160) ?? 'Unnamed MRDS occurrence',
      developmentStatus: finiteString(attrs.dev_stat, 80),
      distanceMi,
      onParcel,
      commodities: commodityNames(attrs),
      ...(sourceUrl ? { sourceUrl } : {}),
    };
    const prior = byId.get(featureId);
    if (!prior || candidate.distanceMi < prior.distanceMi) byId.set(featureId, candidate);
  }

  const all = [...byId.values()]
    .filter(row => Number.isFinite(row.distanceMi) && row.distanceMi <= 5.000_001)
    .sort((a, b) => a.distanceMi - b.distanceMi || a.featureId.localeCompare(b.featureId));
  const commodities = [...new Set(all.flatMap(row => row.commodities))]
    .sort((a, b) => a.localeCompare(b));
  const truncated = payload.exceededTransferLimit === true || all.length > DETAILS_LIMIT;
  const data: MineralResourcesScreenData = {
    occurrenceCountOnParcel: boundary ? all.filter(row => row.onParcel === true).length : null,
    occurrenceCountWithin1Mi: all.filter(row => row.distanceMi <= 1.000_001).length,
    occurrenceCountWithin5Mi: all.length,
    nearestDistanceMi: all[0]?.distanceMi ?? null,
    commodities,
    occurrences: all.slice(0, DETAILS_LIMIT),
    queriedFeatureCount: (payload.features ?? []).length,
    truncated,
    sourceVintage: 'MRDS legacy compilation; individual records are current only as of their cited source reports',
    screeningOnly: true,
  };
  const retrievedAt = new Date().toISOString();
  return {
    data,
    sources: [{
      field: 'mineralResources.publicScreen',
      source: 'USGS Mineral Resource Data System (MRDS) public occurrence screen',
      url: 'https://mrdata.usgs.gov/mrds/',
      retrievedAt,
    }],
  };
}

export const usgsMrdsProvider: DataProvider<MineralResourcesScreenData> = {
  id: 'usgs-mrds',
  name: 'USGS Mineral Resource Data System (MRDS)',
  cacheTtlMs: CACHE_TTL.STANDARD,
  requiresKey: false,
  fetch: (lat, lng, _apiKey, geo) => fetchUsgsMrdsScreen(lat, lng, geo),
};
